/**
 * Bookings on the till: who is coming tonight, and seating them when they do.
 *
 * The question at the door is "who is next, and where are they sitting". So
 * the list is by arrival time, the time is the biggest thing on each row, and
 * the one button is Seat — which opens the tab on their table and goes
 * straight to it, the same tab the room would have opened.
 *
 * Allergies are said on the row in words, before anybody taps: the person who
 * needs to know about the peanuts is the one showing them to the table.
 *
 * Every time here is the site's clock, as the server wrote it (`local_time`).
 * A till in the wrong zone still shows the restaurant's evening.
 */

import { useState } from 'react';

import {
  ALLERGENS,
  ALLERGEN_LABELS,
  BOOKING_STATUS_LABELS,
  canOverride,
  formatMoney,
  isExpected,
  useBookingDay,
  useBookingSlots,
  useCancelBooking,
  useConfirmBooking,
  useGuests,
  useMarkNoShow,
  usePermission,
  useSeatBooking,
  useTakeBooking,
  type Allergen,
  type Booking,
  type BookingTable,
  type Guest,
} from '@restaurant/api-client';
import {
  ActionError,
  Badge,
  Button,
  Empty,
  ErrorNotice,
  Field,
  Input,
  Loading,
  Segmented,
  Sheet,
} from '@restaurant/ui';

import './guest.css';
import './bookings.css';

function allergenLabel(code: string): string {
  return ALLERGEN_LABELS[code as Allergen] ?? code;
}

/** Everybody's allergies at the table: the booking's and the booker's own. */
function bookingAllergies(booking: Booking): string[] {
  return [...new Set([...booking.allergens, ...booking.guest_allergens])].map(allergenLabel);
}

/** A calendar date `days` away. Anchored to UTC so no zone can shift it. */
function shiftDay(day: string, days: number): string {
  const when = new Date(`${day}T00:00:00Z`);
  when.setUTCDate(when.getUTCDate() + days);
  return when.toISOString().slice(0, 10);
}

function people(n: number): string {
  return `${n} ${n === 1 ? 'cover' : 'covers'}`;
}

type Show = 'coming' | 'all';

export function Bookings({
  locationId,
  onOpenOrder,
}: {
  locationId: string | undefined;
  onOpenOrder: (orderId: string) => void;
}) {
  const [date, setDate] = useState<string | undefined>(undefined);
  const [show, setShow] = useState<Show>('coming');
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<Booking | null>(null);
  const day = useBookingDay(locationId, date);
  const mayWrite = usePermission('reservations.booking.write');

  if (day.isPending) return <Loading label="Loading bookings" />;
  if (day.isError) return <ErrorNotice message={day.error.message} />;

  const bookings = day.data.bookings.filter((b) => show === 'all' || isExpected(b));
  // "Today" is the site's trading day as the server answered it; tomorrow is
  // counted from that, never from the till's own calendar.
  const onTomorrow = date !== undefined;
  const tomorrow = onTomorrow ? date : shiftDay(day.data.business_date, 1);
  const current = open ? (day.data.bookings.find((b) => b.id === open.id) ?? open) : null;

  return (
    <div className="wl">
      <header className="wl__head">
        <div className="wl__count">
          <span className="wl__count-value">{day.data.covers}</span>
          <span className="wl__count-label">
            covers booked {onTomorrow ? 'tomorrow' : 'today'}
          </span>
        </div>
        <Segmented
          label="Which day"
          value={onTomorrow ? 'tomorrow' : 'today'}
          onChange={(next) => setDate(next === 'tomorrow' ? tomorrow : undefined)}
          options={[
            { value: 'today', label: 'Today' },
            { value: 'tomorrow', label: 'Tomorrow' },
          ]}
        />
        <Segmented
          label="Which bookings"
          value={show}
          onChange={setShow}
          options={[
            { value: 'coming', label: 'Still to come' },
            { value: 'all', label: 'All' },
          ]}
        />
        {mayWrite && (
          <Button variant="primary" size="lg" onClick={() => setAdding(true)}>
            New booking
          </Button>
        )}
      </header>

      {bookings.length === 0 ? (
        <Empty
          title={show === 'coming' ? 'Nobody else booked' : 'No bookings'}
          description="Bookings taken on the phone or in the back office appear here by time."
        />
      ) : (
        <ul className="wl__list">
          {bookings.map((booking) => {
            const allergies = bookingAllergies(booking);
            return (
              <li key={booking.id}>
                <article
                  className={`wl__party bk__row${allergies.length > 0 ? ' bk__row--allergy' : ''}`}
                >
                  <span className="bk__time tabular">{booking.local_time}</span>
                  <div className="wl__who">
                    <span className="wl__name">{booking.guest_name ?? 'Guest'}</span>
                    <span className="wl__size">
                      {people(booking.party_size)}
                      {booking.table_labels.length > 0
                        ? ` · Table ${booking.table_labels.join(' + ')}`
                        : ' · No table yet'}
                    </span>
                    {allergies.length > 0 && (
                      <span className="bk__allergy">Allergies: {allergies.join(', ')}</span>
                    )}
                    {booking.notes && <span className="wl__note">{booking.notes}</span>}
                    {booking.deposit_required && (
                      <span className="wl__note">
                        {booking.deposit_received
                          ? `Deposit received ${formatMoney(booking.deposit_received)}`
                          : `Deposit asked ${formatMoney(booking.deposit_required)} — not received`}
                      </span>
                    )}
                  </div>
                  <Badge
                    tone={
                      booking.status === 'confirmed'
                        ? 'ok'
                        : booking.status === 'seated'
                          ? 'accent'
                          : booking.status === 'booked'
                            ? 'neutral'
                            : 'warn'
                    }
                  >
                    {BOOKING_STATUS_LABELS[booking.status]}
                  </Badge>
                  <div className="wl__act">
                    {booking.status === 'seated' && booking.order_id ? (
                      <Button onClick={() => onOpenOrder(booking.order_id as string)}>
                        Open the tab
                      </Button>
                    ) : (
                      isExpected(booking) &&
                      mayWrite && (
                        <Button variant="primary" onClick={() => setOpen(booking)}>
                          Seat
                        </Button>
                      )
                    )}
                  </div>
                </article>
              </li>
            );
          })}
        </ul>
      )}

      {adding && locationId && (
        <NewBookingSheet
          locationId={locationId}
          date={date ?? day.data.business_date}
          onDone={() => setAdding(false)}
        />
      )}
      {current && (
        <BookingSheet
          booking={current}
          tables={day.data.tables}
          onOpenOrder={onOpenOrder}
          onDone={() => setOpen(null)}
        />
      )}
    </div>
  );
}

/**
 * One booking: seat them, or the other things that happen to a booking.
 *
 * Seating lists the booked tables first, already picked, and every other
 * table after — a party of four often ends up somewhere else because a
 * two-top came free by the window. Several can be picked; they are pushed
 * together and the tab goes on the first.
 */
function BookingSheet({
  booking,
  tables,
  onOpenOrder,
  onDone,
}: {
  booking: Booking;
  tables: BookingTable[];
  onOpenOrder: (orderId: string) => void;
  onDone: () => void;
}) {
  const seat = useSeatBooking();
  const confirm = useConfirmBooking();
  const cancel = useCancelBooking();
  const noShow = useMarkNoShow();
  const [picked, setPicked] = useState<string[]>(booking.table_ids);
  const [asking, setAsking] = useState<'cancel' | 'no_show' | null>(null);

  const ordered = [
    ...booking.table_ids
      .map((id) => tables.find((t) => t.id === id))
      .filter((t): t is BookingTable => Boolean(t)),
    ...tables.filter((t) => !booking.table_ids.includes(t.id)),
  ];
  const seats = picked.reduce(
    (sum, id) => sum + (tables.find((t) => t.id === id)?.seats ?? 0),
    0,
  );

  function toggle(id: string) {
    setPicked((now) => (now.includes(id) ? now.filter((t) => t !== id) : [...now, id]));
  }

  const allergies = bookingAllergies(booking);

  return (
    <Sheet
      title={`${booking.local_time} · ${booking.guest_name ?? 'Guest'}`}
      onDismiss={onDone}
      actions={
        <>
          <Button
            variant="primary"
            size="lg"
            block
            disabled={picked.length === 0}
            loading={seat.isPending}
            onClick={() =>
              seat.mutate(
                { id: booking.id, tableIds: picked },
                { onSuccess: (done) => onOpenOrder(done.order_id) },
              )
            }
          >
            {picked.length > 1 ? 'Push together and seat' : 'Seat and open the tab'}
          </Button>
        </>
      }
    >
      <p className="wl__sheet-note">
        {people(booking.party_size)}
        {booking.guest_phone ? ` · ${booking.guest_phone}` : ''}
        {picked.length > 0 && ` · ${seats} seats picked`}
      </p>
      {allergies.length > 0 && <p className="bk__allergy">Allergies: {allergies.join(', ')}</p>}
      {booking.guest_dietary && <p className="wl__sheet-note">{booking.guest_dietary}</p>}
      {booking.notes && <p className="wl__sheet-note">{booking.notes}</p>}

      <ul className="wl__tables">
        {ordered.map((table) => (
          <li key={table.id}>
            <button
              type="button"
              className={`wl__table${picked.includes(table.id) ? ' bk__table--on' : ''}`}
              aria-pressed={picked.includes(table.id)}
              onClick={() => toggle(table.id)}
            >
              <span className="wl__table-label">
                Table {table.label}
                {booking.table_ids.includes(table.id) && ' — booked for them'}
              </span>
              <span className="wl__table-seats">{table.seats} seats</span>
            </button>
          </li>
        ))}
      </ul>
      <ActionError error={seat.error} action="Seating them" title="Not seated" />

      <div className="bk__more">
        {booking.status === 'booked' && (
          <Button
            loading={confirm.isPending}
            onClick={() => confirm.mutate({ id: booking.id })}
          >
            They confirmed
          </Button>
        )}
        {asking === null ? (
          <>
            <Button onClick={() => setAsking('cancel')}>They cancelled</Button>
            <Button onClick={() => setAsking('no_show')}>Did not come</Button>
          </>
        ) : asking === 'cancel' ? (
          <>
            <Button
              loading={cancel.isPending}
              onClick={() =>
                cancel.mutate({ id: booking.id, tellGuest: true }, { onSuccess: onDone })
              }
            >
              Cancel and tell them
            </Button>
            <Button
              loading={cancel.isPending}
              onClick={() => cancel.mutate({ id: booking.id }, { onSuccess: onDone })}
            >
              Cancel quietly
            </Button>
            <Button variant="ghost" onClick={() => setAsking(null)}>
              Keep it
            </Button>
          </>
        ) : (
          <>
            <Button
              loading={noShow.isPending}
              onClick={() => noShow.mutate({ id: booking.id }, { onSuccess: onDone })}
            >
              Mark as a no-show
            </Button>
            <Button variant="ghost" onClick={() => setAsking(null)}>
              Keep it
            </Button>
          </>
        )}
      </div>
      <ActionError error={confirm.error ?? cancel.error ?? noShow.error} action="Changing it" />
    </Sheet>
  );
}

/**
 * A booking taken at the till: the phone rings mid-service.
 *
 * The guest is found by name or number, or added with a name; the time is
 * picked from what the day offers, with a full slot said to be full. When the
 * server refuses on one of the restaurant's own rules — outside the hours, a
 * full slot — the refusal is shown with "Book anyway", because the person on
 * the phone may know better. A table already held is never one of those.
 */
export function NewBookingSheet({
  locationId,
  date: startDate,
  onDone,
}: {
  locationId: string;
  date: string;
  onDone: () => void;
}) {
  const [query, setQuery] = useState('');
  const [guest, setGuest] = useState<Guest | null>(null);
  const [newName, setNewName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [size, setSize] = useState('2');
  const [date, setDate] = useState(startDate);
  const [time, setTime] = useState('');
  const [notes, setNotes] = useState('');
  const [allergens, setAllergens] = useState<string[]>([]);
  const found = useGuests(query, { enabled: query.trim().length >= 2 && !guest, limit: 6 });
  const slots = useBookingSlots(locationId, date, Number(size) || 0);
  const take = useTakeBooking();

  const name = guest ? guest.name : newName.trim();

  function book(override: boolean) {
    take.mutate(
      {
        location_id: locationId,
        party_size: Number(size),
        date,
        time,
        ...(guest
          ? { customer_id: guest.id }
          : {
              guest: {
                name: newName.trim(),
                phone: phone.trim() || null,
                email: email.trim() || null,
              },
            }),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        allergens,
        source: 'phone',
        override,
      },
      { onSuccess: onDone },
    );
  }

  return (
    <Sheet
      title="New booking"
      onDismiss={onDone}
      actions={
        <>
          {canOverride(take.error) && (
            <Button size="lg" block loading={take.isPending} onClick={() => book(true)}>
              Book anyway
            </Button>
          )}
          <Button
            variant="primary"
            size="lg"
            block
            disabled={name === '' || !time || Number(size) < 1}
            loading={take.isPending}
            onClick={() => book(false)}
          >
            Book {name || 'them'} {time && `at ${time}`}
          </Button>
        </>
      }
    >
      {guest ? (
        <div className="bk__guest">
          <span className="wl__name">{guest.name}</span>
          <span className="wl__note">
            {[guest.phone, guest.email].filter(Boolean).join(' · ')}
          </span>
          <Button variant="ghost" size="sm" onClick={() => setGuest(null)}>
            Someone else
          </Button>
        </div>
      ) : (
        <>
          <Field
            label="Find the guest"
            hint="Name, phone or email. Or fill in a new guest below."
          >
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoComplete="off"
              placeholder="Maria, or 912 345"
            />
          </Field>
          {found.data && found.data.length > 0 && (
            <ul className="gl">
              {found.data.map((g) => (
                <li key={g.id}>
                  <button type="button" className="gl__row" onClick={() => setGuest(g)}>
                    <span className="gl__name">{g.name}</span>
                    <span className="gl__contact">
                      {[g.phone, g.email].filter(Boolean).join(' · ')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Field label="New guest's name">
            <Input
              value={newName}
              maxLength={120}
              onChange={(e) => setNewName(e.target.value)}
            />
          </Field>
          <Field label="Phone" hint="Optional.">
            <Input
              value={phone}
              inputMode="tel"
              maxLength={40}
              onChange={(e) => setPhone(e.target.value)}
            />
          </Field>
          <Field label="Email" hint="Optional. The confirmation goes here.">
            <Input
              value={email}
              type="email"
              maxLength={254}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
        </>
      )}

      <Field label="Covers">
        <Input
          type="number"
          inputMode="numeric"
          min={1}
          value={size}
          onChange={(e) => setSize(e.target.value)}
        />
      </Field>
      <Field label="Day">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>

      <p className="gf__label">Time</p>
      {slots.isPending && Number(size) > 0 ? (
        <Loading label="Looking at the day" />
      ) : slots.data && slots.data.length === 0 ? (
        <p className="wl__sheet-note">
          No bookings are taken that day. Type a time to book anyway.
        </p>
      ) : (
        <div className="ga" role="group" aria-label="Time">
          {(slots.data ?? []).map((slot) => (
            <button
              key={slot.starts_at}
              type="button"
              className={`ga__chip${slot.local_time === time ? ' is-on' : ''}${slot.full ? ' bk__slot--full' : ''}`}
              aria-pressed={slot.local_time === time}
              onClick={() => setTime(slot.local_time)}
              title={
                slot.full
                  ? 'Full'
                  : slot.table_label
                    ? `Table ${slot.table_label}`
                    : 'No single table free'
              }
            >
              {slot.local_time}
              {slot.full ? ' · full' : slot.table_label ? '' : ' · no table'}
            </button>
          ))}
        </div>
      )}
      <Field label="Or type a time" hint="On the restaurant's clock, like 19:30.">
        <Input
          value={time}
          inputMode="numeric"
          maxLength={5}
          placeholder="19:30"
          onChange={(e) => setTime(e.target.value)}
        />
      </Field>

      <Field label="Notes" hint="Birthday, high chair, by the window.">
        <Input value={notes} maxLength={500} onChange={(e) => setNotes(e.target.value)} />
      </Field>
      <p className="gf__label">Allergies at the table</p>
      <div className="ga" role="group" aria-label="Allergies">
        {ALLERGENS.map((code) => (
          <button
            key={code}
            type="button"
            className={`ga__chip${allergens.includes(code) ? ' is-on' : ''}`}
            aria-pressed={allergens.includes(code)}
            onClick={() =>
              setAllergens((now) =>
                now.includes(code) ? now.filter((c) => c !== code) : [...now, code],
              )
            }
          >
            {ALLERGEN_LABELS[code]}
          </button>
        ))}
      </div>
      <ActionError error={take.error} action="Taking the booking" title="Not booked" />
    </Sheet>
  );
}
